<template>
  <div>
    <Header :header="$t('CREATE_MULTI_OFFER')"> </Header>

    <!-- Final page: Offer sent confirmation -->
    <b-container class="mt-4" v-if="messageBody">
      <div class="mo-success">
        <div class="mo-success-icon">
          <b-icon icon="check" font-scale="2" />
        </div>
        <h3 class="mo-success-title">{{ messageTitle }}</h3>
        <p class="text-muted">{{ messageBody }}</p>
        <div v-if="offersResult" class="mo-success-link">
          <p class="small text-muted mb-1">{{ $t('MO_OFFER_LINK') }}</p>
          <a :href="offersResult.offerLink" target="_blank" rel="noopener noreferrer" class="font-weight-bold">
            {{ offersResult.offerLink }}
          </a>
        </div>
      </div>
      <b-button
        class="mt-4 mb-4"
        size="lg"
        variant="primary"
        @click="startNew()"
        block
      >
        {{ $t('MO_CREATE_NEW') }}
      </b-button>
    </b-container>

    <!-- Error -->
    <b-container class="mt-4" v-if="techError && !messageBody" id="techError">
      <b-alert show variant="danger">
        <h4 class="alert-heading">{{ $t('TECH_ERROR') }}</h4>
        <p class="mt-4">{{ techError }}</p>
      </b-alert>
    </b-container>

    <b-container class="mt-4" v-if="!messageBody">
      <!-- Wizard steps -->
      <div class="mo-wizard" role="tablist">
        <button
          class="mo-wizard-step"
          :class="{ active: activeTab === 0, completed: activeTab === 1 }"
          @click="activeTab === 1 && !creatingOffers ? activeTab = 0 : null"
          type="button"
          role="tab"
          :aria-selected="activeTab === 0"
          aria-controls="step-form"
        >
          <span class="mo-wizard-circle">
            <b-icon v-if="activeTab === 1" icon="check" />
            <span v-else>1</span>
          </span>
          <span class="mo-wizard-label">{{ $t('MO_STEP_APPLICATION') }}</span>
        </button>
        <div class="mo-wizard-line" :class="{ filled: activeTab === 1 }"></div>
        <button
          class="mo-wizard-step"
          :class="{ active: activeTab === 1, disabled: !offersResult }"
          :disabled="!offersResult"
          type="button"
          role="tab"
          :aria-selected="activeTab === 1"
          aria-controls="step-results"
        >
          <span class="mo-wizard-circle">2</span>
          <span class="mo-wizard-label">{{ $t('MO_STEP_OFFER') }}</span>
        </button>
      </div>

      <!-- Step 1: Taotlus (Form) -->
      <div v-if="activeTab === 0" id="step-form" role="tabpanel" class="mt-3">
          <b-form>
            <div class="card-title">
              <div>
                <span class="text-nowrap"><h2>{{ $t('OFFER') }}</h2></span>
              </div>
              <div class="align-items-end" v-if="totalPrice">
                <Kickback
                  :partner="partner"
                  :contractAmount="new Number(totalPrice)"
                  :contractFee="new Number(contractFee)"
                />
              </div>
            </div>

            <b-card class="card-padding-md">
              <b-row>
                <b-col md="6">
                  <b-form-group
                    :label="$t('SELLER_OFFER_NR') + ':'"
                    label-for="offerNumber"
                  >
                    <b-form-input
                      id="offerNumber"
                      name="offerNumber"
                      maxlength="30"
                      type="text"
                      v-model.trim="offerNumber"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="6" v-if="this.partner && this.partner.editContractFee">
                  <b-form-group label-for="contractFee">
                    <label for="contractFee" class="d-block">
                      {{ $t('SET_CONTRACT_FEE') + ':' }}
                      <b-icon class="pl-0" id="tt-cf" icon="question-circle" />
                      <b-tooltip target="tt-cf" variant="dark" triggers="hover">
                        {{ $t('CONTRACT_FEE_TOOLTIP') }}
                      </b-tooltip>
                    </label>
                    <b-form-input
                      id="contractFee"
                      name="contractFee"
                      type="text"
                      v-model.trim="contractFee"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col md="6">
                  <b-form-group
                    :label="$t('OFFER_PRICE_WITH_VAT') + ':'"
                    label-for="totalPriceWithVat"
                    :hidden="user && user.hideVatFields"
                  >
                    <b-form-input
                      id="totalPriceWithVat"
                      name="totalPriceWithVat"
                      v-model.trim="totalPriceWithVat"
                      type="text"
                      @input="calculateWithoutVat"
                    ></b-form-input>
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group
                    :label="$t('SET_OFFER_PRICE_NO_VAT') + ':'"
                    label-for="totalPrice"
                  >
                    <b-form-input
                      id="totalPrice"
                      name="totalPrice"
                      v-model.trim="totalPrice"
                      type="text"
                      @input="calculateWithVat"
                    ></b-form-input>
                  </b-form-group>
                </b-col>
              </b-row>
            </b-card>

            <div class="mt-3" v-if="totalMainAssetsAmount > totalPrice">
              <b-alert show variant="info">
                Taotluse summa peab olema suurem v&otilde;i v&otilde;rdne varade
                koguv&auml;&auml;rtusega.<br /><br />
                - taotluse summa: {{ totalPrice || 0 }}<br />
                - varade koguv&auml;&auml;rtus: {{ totalMainAssetsAmount }}
              </b-alert>
            </div>

            <h2 class="card-title mt-4">
              <span class="text-nowrap">{{ $t('MAIN_ASSETS') }}</span>
            </h2>
            <b-card class="card-padding-md">
              <b-row v-for="(asset, index) in assets" v-bind:key="index">
                <b-col md="6">
                  <b-form-group
                    :label="$t('CHOOSE_ASSET_GROUP') + ':'"
                    label-for="assetSubGroup"
                  >
                    <multiselect
                      v-model="asset.group"
                      :options="assetGroups"
                      :allow-empty="false"
                      :show-labels="false"
                      :searchable="false"
                      placeholder="Vali väärtus"
                    >
                      <template #option="{ option }">
                        {{ option.name }}
                      </template>
                      <template #singleLabel="{ option }">
                        {{ option.name }}
                      </template>
                    </multiselect>
                  </b-form-group>
                </b-col>

                <b-col md="6">
                  <b-form-group
                    :label="$t('SET_ASSET_PRICE_NO_VAT') + ':'"
                    label-for="assetPrice"
                  >
                    <div class="d-flex">
                      <b-form-input
                        id="assetPrice"
                        name="assetPrice"
                        type="text"
                        v-model.trim="asset.assetPrice"
                        @change="replaceAssetCommas"
                      />
                      <div class="ml-2 mt-1" v-if="index > 0">
                        <b-button
                          variant="secondary"
                          @click="removeMainAsset(index)"
                        >
                          <b-icon icon="trash" />
                        </b-button>
                      </div>
                    </div>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col md="6">
                  <b-button variant="secondary" @click="addMainAsset()">
                    <b-icon icon="plus" />
                  </b-button>
                  <span class="ml-1">Lisa uus rida</span>
                </b-col>
              </b-row>
              <b-row
                v-if="additionalAssetsPrice > 0 && (totalMainAssetsAmount || 0) > 0"
                class="mt-3"
              >
                <b-col md="6"></b-col>
                <b-col md="6">
                  <b-form-group
                    :label="$t('ADDITIONAL_ASSETS_COST') + ':'"
                    label-for="additionalAssetPrice"
                  >
                    <b-form-input
                      id="additionalAssetPrice"
                      name="additionalAssetPrice"
                      :value="additionalAssetsPrice"
                      type="text"
                      disabled
                    />
                  </b-form-group>
                </b-col>
              </b-row>
            </b-card>

            <h2 class="card-title mt-4">
              <span class="text-nowrap">{{ $t('CLIENT') }}</span>
            </h2>
            <b-card class="card-padding-md">
              <b-row>
                <b-col md="6">
                  <b-form-group
                    :label="$t('CUSTOMER_NAME_NOT_REQUIRED') + ':'"
                    label-for="customerName"
                  >
                    <multiselect
                      v-model="selected"
                      id="customerName"
                      :options="companies"
                      :loading="isLoading"
                      label="name"
                      @search-change="getCompaniesByName"
                      @close="setCompanyParams"
                      :placeholder="$t('SEARCH')"
                      :show-labels="false"
                    >
                      <template slot="noResult">
                        {{ $t('NO_ELEMENTS_FOUND') }}
                      </template>
                    </multiselect>
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group
                    :label="$t('REGISTRY_CODE') + ':'"
                    label-for="customerRegCode"
                  >
                    <multiselect
                      v-model="selected"
                      id="customerRegCode"
                      :options="companies"
                      :loading="isLoading"
                      label="companyCode"
                      @search-change="getCompaniesByCode"
                      @close="setCompanyParams"
                      :placeholder="$t('SEARCH')"
                      :show-labels="false"
                    >
                      <template slot="noResult">
                        {{ $t('NO_ELEMENTS_FOUND') }}
                      </template>
                    </multiselect>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col md="6">
                  <b-form-group
                    :label="$t('EMAIL') + ':'"
                    label-for="contactEmail"
                  >
                    <b-form-input
                      id="contactEmail"
                      name="contactEmail"
                      type="text"
                      v-model.trim="contactEmail"
                    />
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group :label="$t('PHONE') + ':'" label-for="contactPhone">
                    <div class="d-flex">
                      <b-input-group-prepend is-text>+372</b-input-group-prepend>
                      <b-form-input
                        id="contactPhone"
                        name="contactPhone"
                        type="text"
                        v-model.trim="contactPhone"
                        class="ml-1"
                      ></b-form-input>
                    </div>
                  </b-form-group>
                </b-col>
              </b-row>
            </b-card>

            <b-button
              class="mt-4 mb-4"
              size="lg"
              :disabled="!validToSubmit || creatingOffers"
              variant="primary"
              @click="createOffers()"
              block
            >
              <span v-if="!creatingOffers">Koosta pakkumine</span>
              <span v-if="creatingOffers">Koostan pakkumist. Palun oota...</span>
              <span
                class="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
                v-if="creatingOffers"
              ></span>
            </b-button>
          </b-form>
      </div>

      <!-- Step 2: Pakkumine (Results) -->
      <div v-if="activeTab === 1" id="step-results" role="tabpanel" class="mt-3">
          <div v-if="offersResult">
            <!-- Offer cards -->
            <b-row class="mb-4">
              <b-col
                md="6"
                v-for="(offer, index) in offersResult.offers"
                :key="index"
                class="d-flex"
              >
                <b-card
                  class="card-padding-md mo-offer-card"
                  :class="{
                    'mo-offer-card--active': index === activeOfferIndex,
                    'mo-offer-card--inactive': index !== activeOfferIndex,
                  }"
                  tabindex="0"
                  role="button"
                  :aria-pressed="index === activeOfferIndex"
                  :aria-label="$t('MO_SELECT_OFFER', { name: getOfferName(offer) })"
                  @click="activeOfferIndex = index"
                  @keydown.enter="activeOfferIndex = index"
                  @keydown.space.prevent="activeOfferIndex = index"
                >
                  <!-- Recommended badge for LITE -->
                  <div v-if="offer.product === 'LITE'" class="mo-badge">
                    {{ $t('MO_RECOMMENDED') }}
                  </div>

                  <!-- Offer name -->
                  <h3 class="h5 font-weight-bold mb-3">{{ getOfferName(offer) }}</h3>

                  <!-- Price display -->
                  <div class="d-flex align-items-baseline" style="gap: 4px">
                    <span class="mo-card__price">{{ getDisplayPrice(offer, index) }}&euro;</span>
                    <span class="small text-muted">/{{ $t('MO_MONTH') }} + {{ $t('MO_VAT') }}</span>
                  </div>
                  <p class="small text-muted mb-3">
                    {{ getSelectedMonths(index) }} {{ $t('MO_MONTHLY_PAYMENT') }}
                  </p>

                  <hr />

                  <!-- Period pills -->
                  <p class="mo-section-label">{{ $t('MO_RENTAL_PERIOD') }}</p>
                  <div class="d-flex flex-wrap mb-3" style="gap: 8px">
                    <b-button
                      v-for="item in offer.items"
                      :key="item.months"
                      pill
                      size="sm"
                      :variant="getSelectedMonths(index) === item.months ? 'primary' : 'light'"
                      class="mo-period-pill"
                      @click.stop="selectPeriod(index, item.months)"
                    >
                      {{ item.months }} {{ $t('MO_MONTHS') }}
                    </b-button>
                  </div>

                  <hr />

                  <!-- Contract details -->
                  <p class="mo-section-label">{{ $t('MO_CONTRACT') }}</p>
                  <div class="d-flex flex-column mb-2" style="gap: 8px">
                    <div class="d-flex align-items-center small" style="gap: 8px">
                      <b-icon icon="calendar" class="text-brand flex-shrink-0" />
                      <span>{{ getContractDetails(offer).start }}</span>
                    </div>
                    <div class="d-flex align-items-center small" style="gap: 8px">
                      <b-icon icon="clock" class="text-brand flex-shrink-0" />
                      <span>{{ getContractDetails(offer).end }}</span>
                    </div>
                  </div>

                  <!-- Feature list -->
                  <ul class="list-unstyled mt-auto mb-0">
                    <li
                      v-for="feature in getFeatures(offer)"
                      :key="feature"
                      class="d-flex align-items-start small text-muted mb-2"
                      style="gap: 8px"
                    >
                      <b-icon icon="check" class="text-accent flex-shrink-0" />
                      <span>{{ feature }}</span>
                    </li>
                  </ul>
                </b-card>
              </b-col>
            </b-row>

            <!-- Transaction summary -->
            <b-card v-if="totalAssetCost > 0" class="card-padding-md text-center my-4">
              <h4 class="font-weight-bold mb-4">{{ $t('MO_TRANSACTION_SUMMARY') }}</h4>
              <b-row class="justify-content-center">
                <b-col cols="auto" class="text-center">
                  <span class="d-block small text-muted">{{ $t('MO_ASSET_COST') }}</span>
                  <span class="d-block h4 font-weight-bold text-brand">{{ formatPrice(totalAssetCost) }}&euro;</span>
                  <span class="d-block small text-muted">{{ $t('MO_EXCL_VAT') }}</span>
                </b-col>
                <b-col cols="auto" class="text-center" v-if="residualValue != null">
                  <span class="d-block small text-muted">{{ $t('MO_RESIDUAL_PURCHASE') }}</span>
                  <span class="d-block h4 font-weight-bold text-brand">{{ formatPrice(residualValue) }}&euro;</span>
                  <span class="d-block small text-muted">{{ $t('MO_AT_CONTRACT_END') }}</span>
                </b-col>
              </b-row>
            </b-card>

            <!-- Comparison section -->
            <div v-if="offersResult.offers.length > 1" class="my-4">
              <h4 class="text-center font-weight-bold mb-4">{{ $t('MO_COMPARISON_TITLE') }}</h4>
              <b-row>
                <b-col md="6" v-for="(offer, index) in offersResult.offers" :key="'cmp-'+index">
                  <b-card class="mb-3">
                    <h5>{{ getOfferName(offer) }}</h5>
                    <ul class="list-unstyled mb-0">
                      <li
                        v-for="diff in getComparisonPoints(offer)"
                        :key="diff.title"
                        class="d-flex mb-3 small"
                        style="gap: 8px; line-height: 1.4"
                      >
                        <span class="text-accent flex-shrink-0">&rarr;</span>
                        <span><strong>{{ diff.title }}</strong> &ndash; {{ diff.desc }}</span>
                      </li>
                    </ul>
                  </b-card>
                </b-col>
              </b-row>
            </div>

            <!-- Assets list -->
            <b-card v-if="allDisplayAssets.length > 0" no-body class="my-4">
              <b-card-header
                class="d-flex align-items-center justify-content-between bg-white cursor-pointer"
                @click="assetsExpanded = !assetsExpanded"
              >
                <div class="d-flex align-items-center" style="gap: 10px">
                  <b-icon icon="box" class="text-brand" />
                  <span class="font-weight-bold small">{{ $t('MO_RENTED_ASSETS') }} ({{ allDisplayAssets.length }})</span>
                </div>
                <b-icon :icon="assetsExpanded ? 'chevron-up' : 'chevron-down'" class="text-muted" />
              </b-card-header>
              <b-collapse v-model="assetsExpanded">
                <b-list-group flush>
                  <b-list-group-item
                    v-for="(asset, i) in allDisplayAssets"
                    :key="i"
                    class="d-flex justify-content-between align-items-center small"
                  >
                    <span>{{ asset.name }}</span>
                    <span class="font-weight-bold">{{ formatPrice(asset.price) }}&euro;</span>
                  </b-list-group-item>
                </b-list-group>
              </b-collapse>
            </b-card>

            <!-- Offer link -->
            <div class="text-center mt-4 mb-3">
              <p class="text-muted small">{{ $t('MO_OFFER_LINK') }}</p>
              <a :href="offersResult.offerLink" target="_blank" rel="noopener noreferrer" class="font-weight-bold">
                {{ offersResult.offerLink }}
              </a>
            </div>

            <!-- Footer -->
            <div class="text-center text-brand small mt-4 pb-3">
              <p>{{ $t('MO_FOOTER_CONTACT') }}</p>
            </div>

            <!-- Send button -->
            <b-button
              class="mt-3 mb-4"
              size="lg"
              :disabled="!validToSend || sendingOffers"
              variant="primary"
              @click="sendOffers()"
              block
            >
              <span v-if="!sendingOffers">{{ $t('MO_SEND_OFFER') }}</span>
              <span v-if="sendingOffers">{{ $t('MO_SENDING_OFFER') }} </span>
              <span
                class="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
                v-if="sendingOffers"
              ></span>
            </b-button>
          </div>
      </div>
    </b-container>
    <Footer />
  </div>
</template>

<script>
import { Header, Footer } from '@/components/common';
import axios from 'axios';
import Multiselect from 'vue-multiselect';
import { replaceComma } from '@/utils';
import { getPriceWithoutVat, getPriceWithVat } from '../../utils';
import { mapActions, mapState } from 'vuex';
import { GET_BY_NAME, GET_BY_COMPANY_CODE } from '../../store/company';
import i18n from '../../i18n';
import Kickback from '@/components/common/Kickback';

export default {
  name: 'SelfServeMultiOffer',
  components: {
    Header,
    Footer,
    Multiselect,
    Kickback,
  },
  async mounted() {
    await axios.get('user/get-logged-in-user').then(async ({ data: userData }) => {
      if (userData.partnerId) {
        this.user = userData;
        this.partner = userData.partner;
        if (this.partner.editContractFee) {
          this.contractFee = this.partner.contractFee;
        }
        await axios
          .get('/partner/' + userData.partnerId + '/asset-group/names')
          .then(({ data }) => {
            this.assetGroups = data;
          });
      }
    });

    // Load existing multi-offer if queryId is in route
    if (this.$route.params.queryId) {
      this.loadExistingOffer(this.$route.params.queryId);
    }
  },
  methods: {
    ...mapActions('company', [GET_BY_NAME, GET_BY_COMPANY_CODE]),
    calculateWithoutVat(price) {
      this.totalPrice = getPriceWithoutVat(price);
    },
    calculateWithVat(price) {
      this.totalPriceWithVat = getPriceWithVat(price);
    },
    removeMainAsset(index) {
      this.assets.splice(index, 1);
    },
    addMainAsset() {
      this.assets.push({});
    },
    replaceAssetCommas() {
      this.assets = this.assets.map(asset => ({
        ...asset,
        assetPrice: replaceComma(asset.assetPrice),
      }));
    },
    setCompanyParams() {
      this.customerRegCode = this.selected.companyCode;
      this.customerName = this.selected.name;
    },
    async getCompaniesByName(query) {
      if (query.length > 2) {
        this.isLoading = true;
        this.GET_BY_NAME(query).then(() => {
          this.isLoading = false;
        });
      }
    },
    async getCompaniesByCode(query) {
      if (query.length > 2) {
        this.isLoading = true;
        await this.GET_BY_COMPANY_CODE(query);
        this.isLoading = false;
      }
    },
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },

    async createOffers() {
      this.creatingOffers = true;
      this.techError = null;
      try {
        const payload = {
          customerName: this.customerName,
          customerRegCode: this.customerRegCode,
          email: this.contactEmail,
          phone: this.contactPhone,
          offerNumber: this.offerNumber,
          totalAmountExclVat: this.totalPrice,
          contractFee: this.contractFee,
          mainAssets: this.assets.map(a => ({
            group: a.group ? a.group.name : null,
            name: a.group ? a.group.name : null,
            priceExclVat: a.assetPrice,
          })),
        };
        const { data } = await axios.post('/multi-offers', payload);

        if (!data.offers || data.offers.length === 0) {
          this.techError = data.message || i18n.t('OFFER_CREATION_FAILED');
          this.scrollToTop();
        } else {
          this.offersResult = data;
          // Initialize period selection for each offer
          this.offerStates = data.offers.map(offer => ({
            selectedMonths:
              offer.items && offer.items.length > 0
                ? offer.items[0].months
                : null,
          }));
          this.activeOfferIndex = 0;
          this.$nextTick(() => {
            this.activeTab = 1;
            this.scrollToTop();
          });
        }
      } catch (error) {
        this.techError = i18n.t('OFFER_CREATION_FAILED');
        this.scrollToTop();
      } finally {
        this.creatingOffers = false;
      }
    },

    async sendOffers() {
      this.sendingOffers = true;
      this.techError = null;
      try {
        await axios.put('/multi-offers/send', {
          queryIds: this.offersResult.offers.map(o => o.queryId),
          offerLink: this.offersResult.offerLink,
          email: this.contactEmail,
          phone: this.contactPhone,
        });

        this.messageTitle = i18n.t('MO_OFFER_SENT_TITLE');
        if (this.contactPhone && this.contactEmail) {
          this.messageBody = i18n.t('MO_OFFER_SENT_EMAIL_PHONE', {
            email: this.contactEmail,
            phone: this.contactPhone,
          });
        } else if (this.contactPhone) {
          this.messageBody = i18n.t('MO_OFFER_SENT_PHONE', {
            phone: this.contactPhone,
          });
        } else if (this.contactEmail) {
          this.messageBody = i18n.t('MO_OFFER_SENT_EMAIL', {
            email: this.contactEmail,
          });
        }
        this.scrollToTop();
      } catch (error) {
        this.techError = i18n.t('OFFER_SENDING_FAILED');
        this.scrollToTop();
      } finally {
        this.sendingOffers = false;
      }
    },

    startNew() {
      this.offersResult = null;
      this.offerStates = [];
      this.messageTitle = null;
      this.messageBody = null;
      this.techError = null;
      this.activeTab = 0;
      this.offerNumber = '';
      this.totalPrice = null;
      this.totalPriceWithVat = null;
      this.assets = [{}];
      this.contactEmail = null;
      this.contactPhone = null;
      this.customerName = null;
      this.customerRegCode = null;
      this.selected = [];
    },

    async loadExistingOffer(queryId) {
      try {
        const { data } = await axios.get('/multi-offers/by-query/' + queryId);
        this.offersResult = data;
        this.offerStates = data.offers.map(offer => ({
          selectedMonths:
            offer.items && offer.items.length > 0
              ? offer.items[0].months
              : null,
        }));
        this.activeOfferIndex = 0;
        this.activeTab = 1;

        // Populate form fields from loaded data
        var firstOffer = data.offers[0];
        if (firstOffer) {
          this.customerName = firstOffer.customerName;
          this.customerRegCode = firstOffer.customerRegCode;
        }
        if (data.offerNumber) {
          this.offerNumber = data.offerNumber;
        }
        if (data.totalPrice) {
          this.totalPrice = data.totalPrice;
          this.totalPriceWithVat = getPriceWithVat(data.totalPrice);
        }
        if (data.contactEmail) {
          this.contactEmail = data.contactEmail;
        }
        if (data.contactPhone) {
          this.contactPhone = data.contactPhone;
        }
        if (data.mainAssets && data.mainAssets.length > 0) {
          this.assets = data.mainAssets.map(a => ({
            group: this.assetGroups.find(g => g.assetGroupId === a.groupId) || { name: a.name },
            assetPrice: a.price,
          }));
        }
      } catch (error) {
        this.techError = i18n.t('OFFER_CREATION_FAILED');
      }
    },

    // Results display helpers
    getOfferName(offer) {
      if (offer.product === 'LITE') return 'Light';
      return 'Pro';
    },
    getSelectedMonths(index) {
      if (this.offerStates[index]) return this.offerStates[index].selectedMonths;
      return null;
    },
    selectPeriod(index, months) {
      this.$set(this.offerStates, index, { selectedMonths: months });
    },
    getSelectedItem(index) {
      const offer = this.offersResult.offers[index];
      if (!offer || !offer.items) return null;
      const months = this.getSelectedMonths(index);
      return offer.items.find(item => item.months === months) || offer.items[0];
    },
    getDisplayPrice(offer, index) {
      const item = this.getSelectedItem(index);
      if (!item) return '\u2013';
      const price = item.priceWithoutInsurance;
      if (price == null) return '\u2013';
      return Number(price).toFixed(2).replace('.', ',');
    },
    getContractDetails(offer) {
      if (offer.product === 'LITE') {
        return {
          start: i18n.t('MO_LITE_CONTRACT_START'),
          end: i18n.t('MO_LITE_CONTRACT_END'),
        };
      }
      return {
        start: i18n.t('MO_PRO_CONTRACT_START'),
        end: i18n.t('MO_PRO_CONTRACT_END'),
      };
    },
    getFeatures(offer) {
      if (offer.product === 'LITE') {
        return [
          i18n.t('MO_LITE_FEATURE_1'),
          i18n.t('MO_LITE_FEATURE_2'),
          i18n.t('MO_LITE_FEATURE_3'),
          i18n.t('MO_LITE_FEATURE_4'),
        ];
      }
      return [
        i18n.t('MO_PRO_FEATURE_1'),
        i18n.t('MO_PRO_FEATURE_2'),
        i18n.t('MO_PRO_FEATURE_3'),
        i18n.t('MO_PRO_FEATURE_4'),
      ];
    },
    getComparisonPoints(offer) {
      if (offer.product === 'LITE') {
        return [
          { title: i18n.t('MO_LITE_CONTRACT_START'), desc: i18n.t('MO_LITE_CMP_START_DESC') },
          { title: i18n.t('MO_LITE_CONTRACT_END'), desc: i18n.t('MO_LITE_CMP_END_DESC') },
        ];
      }
      return [
        { title: i18n.t('MO_PRO_CONTRACT_START'), desc: i18n.t('MO_PRO_CMP_START_DESC') },
        { title: i18n.t('MO_PRO_CMP_END_TITLE'), desc: i18n.t('MO_PRO_CMP_END_DESC') },
      ];
    },
    formatPrice(price) {
      if (price == null) return '\u2013';
      return Number(price).toFixed(2).replace('.', ',');
    },
  },
  data() {
    return {
      partner: null,
      user: null,
      contractFee: null,
      totalPriceWithVat: null,
      totalPrice: null,
      offerNumber: '',
      assets: [{}],
      assetGroups: [],
      selected: [],
      isLoading: false,
      contactEmail: null,
      contactPhone: null,
      customerRegCode: null,
      customerName: null,
      creatingOffers: false,
      sendingOffers: false,
      activeTab: 0,
      offersResult: null,
      offerStates: [],
      activeOfferIndex: 0,
      assetsExpanded: false,
      messageTitle: null,
      messageBody: null,
      techError: null,
    };
  },
  watch: {
    totalPriceWithVat(value) {
      this.totalPriceWithVat = replaceComma(value);
    },
    totalPrice(value) {
      this.totalPrice = replaceComma(value);
    },
  },
  computed: {
    ...mapState('company', ['companies']),
    validToSubmit() {
      if (this.totalPrice <= 0) return false;
      if (this.totalMainAssetsAmount > this.totalPrice) return false;
      if (this.assets.length === 0) return false;
      if (this.offerNumber.length === 0) return false;
      if (this.assets.some(asset => !asset.group)) return false;
      if (this.assets.some(asset => !asset.assetPrice || asset.assetPrice <= 0))
        return false;
      return true;
    },
    validToSend() {
      return this.validToSubmit && (this.contactPhone || this.contactEmail);
    },
    allDisplayAssets() {
      const items = this.assets
        .filter(a => a.assetPrice)
        .map(a => ({
          name: a.group ? a.group.name : 'Vara',
          price: parseFloat(a.assetPrice || 0),
        }));
      if (this.additionalAssetsPrice > 0) {
        items.push({ name: 'Lisa', price: this.additionalAssetsPrice });
      }
      return items;
    },
    additionalAssetsPrice() {
      const additionalAssetsPrice =
        (this.totalPrice || 0) - (this.totalMainAssetsAmount || 0);
      return Number(additionalAssetsPrice.toFixed(2));
    },
    totalMainAssetsAmount() {
      return this.assets.reduce((acc, asset) => {
        return acc + parseFloat(asset.assetPrice || 0);
      }, 0);
    },
    totalAssetCost() {
      if (!this.offersResult || !this.offersResult.offers || this.offersResult.offers.length === 0) return 0;
      const offer = this.offersResult.offers[0];
      return Number(offer.assetPrice || 0);
    },
    residualValue() {
      const item = this.getSelectedItem(this.activeOfferIndex);
      if (!item || item.residualPrc == null) return null;
      return this.totalAssetCost * Number(item.residualPrc);
    },
  },
};
</script>

<style scoped>
/* Wizard steps */
.mo-wizard {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem 0 0.5rem;
  margin-bottom: 0.5rem;
}

.mo-wizard-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  min-width: 90px;
}

.mo-wizard-step.disabled {
  cursor: default;
}

.mo-wizard-circle {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  font-weight: 700;
  border: 2px solid #dcdede;
  background: #fff;
  color: #6c757d;
  transition: all 0.25s;
}

.mo-wizard-step.active .mo-wizard-circle {
  background: #822460;
  border-color: #822460;
  color: #fff;
  box-shadow: 0 2px 8px rgba(130, 36, 96, 0.3);
}

.mo-wizard-step.completed .mo-wizard-circle {
  background: #822460;
  border-color: #822460;
  color: #fff;
}

.mo-wizard-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6c757d;
  transition: color 0.2s;
}

.mo-wizard-step.active .mo-wizard-label {
  color: #822460;
}

.mo-wizard-step.completed .mo-wizard-label {
  color: #822460;
}

.mo-wizard-step.disabled .mo-wizard-label {
  opacity: 0.5;
}

.mo-wizard-step.disabled .mo-wizard-circle {
  opacity: 0.5;
}

.mo-wizard-line {
  flex: 1;
  max-width: 120px;
  height: 2px;
  background: #dcdede;
  margin: 0 0.75rem;
  margin-bottom: 22px;
  transition: background 0.3s;
}

.mo-wizard-line.filled {
  background: #822460;
}

/* Success state */
.mo-success {
  text-align: center;
  padding: 2.5rem 1.5rem 1.5rem;
  background: #fff;
  border: 1px solid #dcdede;
  border-radius: 7px;
}

.mo-success-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #822460;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.25rem;
}

.mo-success-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 0.5rem;
}

.mo-success-link {
  margin-top: 1.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid #dcdede;
}

/* Offer cards */
.mo-offer-card {
  position: relative;
  cursor: pointer;
  transition: opacity 0.2s, box-shadow 0.2s;
  flex: 1;
}

.mo-offer-card ::v-deep .card-body {
  display: flex;
  flex-direction: column;
}

.mo-offer-card ::v-deep .card-body > hr {
  width: 100%;
}

.mo-offer-card--active {
  box-shadow: inset 0 0 0 2px #822460;
}

.mo-offer-card--inactive {
  opacity: 0.65;
}

.mo-offer-card--inactive:hover {
  opacity: 0.85;
}

/* Badge */
.mo-badge {
  position: absolute;
  top: -12px;
  left: 1.5rem;
  background: #822460;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 4px 14px;
  border-radius: 20px;
}

/* Price */
.mo-card__price {
  font-size: 2.5rem;
  font-weight: 700;
  color: #822460;
}

/* Section labels */
.mo-section-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.5rem;
}

/* Period pills */
.mo-period-pill {
  font-weight: 500;
}

.mo-period-pill.btn-light {
  color: #822460;
}

/* Responsive */
@media (max-width: 768px) {
  .mo-card__price {
    font-size: 2rem;
  }

  .mo-period-pill {
    min-height: 44px;
    display: inline-flex;
    align-items: center;
  }
}
</style>
